import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoneIcon from '@mui/icons-material/Done';
import { IUserSummary } from '../../types/global/helper';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { userSearchSelector, searchingUserSearchSelector } from '../AvatarGroup/selector';
import { searchUser } from '../../redux/user/actions';
import _ from 'lodash';
import { theme } from '../../constants/theme';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { showToastMessage } from '../../utils/AppUtils';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface TagPeopleModalProps {
    onBack: () => void;
    initialUsers?: IUserSummary[];
    onChange?: (users: IUserSummary[]) => void;
}

const TagPeopleModal: React.FC<TagPeopleModalProps> = ({ onBack, initialUsers = [], onChange }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<IUserSummary[]>(initialUsers);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);

    const handleGetUsers = useCallback(async () => {
        dispatch(searchUser({ searchTerm: searchQuery }));
    }, [dispatch, searchQuery]);

    useLayoutEffect(() => {
        handleGetUsers();
    }, [handleGetUsers]);

    const handleSearchDebounced = useRef(
        _.debounce(searchTerm => {
            if (searchTerm.length > 3 && !searchingUser) {
                dispatch(searchUser({ searchTerm }));
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchValue = event.target.value;
        setSearchQuery(searchValue);
        handleSearchDebounced(searchValue);
    };

    const toggleSelectUser = (user: IUserSummary) => {
        const isChecked = selectedUsers?.some(a => a._id === user._id);
        if (isChecked) {
            const initial = selectedUsers.filter(u => u._id !== user._id);
            setSelectedUsers(initial);
        } else {
            if (selectedUsers.length < 9) {
                setSelectedUsers(prevState => [...prevState, user]);
            } else {
                showToastMessage(`Maximum of 10 test users reached`);
            }
        }
    };

    const handleOnChange = () => {
        onChange && onChange(selectedUsers);
        onBack();
    };

    return (
        <Box sx={{ width: '100%', flexShrink: 0, height: '500px' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}>
                <IconButton onClick={onBack} sx={{ color: 'white' }}>
                    <ArrowBackIcon sx={{ color: theme[themeStyle].tag_white }} />
                </IconButton>
                <Typography variant="h6">Tag people</Typography>
                <Button
                    onClick={handleOnChange}
                    variant="text"
                    startIcon={<DoneIcon />}
                    sx={{ color: '#4267B2', textTransform: 'none' }}>
                    Done
                </Button>
            </Box>

            {/* Search Bar */}
            <TextField
                placeholder="Search"
                fullWidth
                value={searchQuery}
                onChange={handleSearchInputChange}
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: theme[themeStyle].darkGray60,
                        borderRadius: '10px',
                        color: theme[themeStyle].tag_white,
                    },
                    '& .MuiOutlinedInput-input': {
                        color: theme[themeStyle].tag_white,
                    },
                }}
            />

            {/* Suggestions */}
            <Typography sx={{ color: '#aaaaaa', fontSize: '14px', mb: 1 }}>SUGGESTIONS</Typography>

            {/* Scrollable List */}
            <Box
                sx={{
                    height: 'calc(500px - 120px)', // Adjust based on header and search bar height
                    overflowY: 'auto',
                }}>
                <List>
                    {userResults && userResults.length > 0 ? (
                        userResults.map(user => {
                            const isAlreadySelected = selectedUsers.find(u => u._id === user._id);
                            return (
                                <ListItem
                                    key={user._id}
                                    sx={{
                                        backgroundColor: !!isAlreadySelected
                                            ? theme[themeStyle].darkGray60
                                            : 'transparent',
                                        borderRadius: '10px',
                                        mb: 1,
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].darkGray60,
                                        },
                                    }}
                                    onClick={() => toggleSelectUser(user)}>
                                    <ListItemAvatar>
                                        <ThspianAvatar
                                            user={user}
                                            sx={{
                                                width: 50,
                                                height: 50,
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={user.firstName}
                                        secondary={user.userName}
                                        secondaryTypographyProps={{
                                            sx: {
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                        }}
                                        sx={{ color: theme[themeStyle].tag_white }}
                                    />
                                    {!!isAlreadySelected && <DoneIcon sx={{ color: '#4267B2' }} />}
                                </ListItem>
                            );
                        })
                    ) : (
                        <Typography color="white" sx={{ textAlign: 'center', mt: 2 }}>
                            No user found
                        </Typography>
                    )}
                </List>
            </Box>
        </Box>
    );
};

export default TagPeopleModal;
